import type { Document, OwnerTypeEnum, UploadRequestResponse } from '../../../js/api';
import { DocumentsService } from '../../../js/api';

export type DocumentUploadArgs = {
  file: File;
  documentTypeSlug: string;
  ownerType: OwnerTypeEnum;
  ownerId: string;
  contextType: string;
  contextId: string;
  relatedProviderId?: string;
  label?: string;
  /** ISO dates (yyyy-MM-dd); only expirable types need them. */
  issuedOn?: string | null;
  expiresOn?: string | null;
  /**
   * Signature-requiring types only: true starts e-sign, false records an already-signed
   * manual upload. Ignored by the backend for other types.
   */
  requestEsignature?: boolean;
};

/**
 * The three steps of a documents-framework upload — presigned request, PUT to S3,
 * confirm — as one promise resolving to the confirmed Document. The upload dialog and
 * any form that uploads a file as part of saving something else (a credential entry
 * with its backing document) share this so the steps never drift.
 */
export async function uploadDocument({
  file,
  documentTypeSlug,
  ownerType,
  ownerId,
  contextType,
  contextId,
  relatedProviderId,
  label,
  issuedOn,
  expiresOn,
  requestEsignature = true,
}: DocumentUploadArgs): Promise<Document> {
  // Step 1: Request a presigned upload URL
  const uploadResponse: UploadRequestResponse = await DocumentsService.documentsUploadRequestCreate(
    {
      requestBody: {
        document_type: documentTypeSlug,
        original_filename: file.name,
        file_size: file.size,
        mime_type: file.type,
        owner_type: ownerType,
        owner_id: ownerId,
        related_provider_id: relatedProviderId,
        context_type: contextType,
        context_id: contextId,
        label: label || undefined,
        issued_on: issuedOn ?? undefined,
        expires_on: expiresOn ?? undefined,
      },
    }
  );

  // Step 2: PUT file to S3 with the presigned URL
  const putResponse = await fetch(uploadResponse.upload_url, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!putResponse.ok) {
    throw new Error(`S3 upload failed with status ${putResponse.status}`);
  }

  // Step 3: Confirm the upload. request_esignature is ignored by the backend
  // for non-signature types; for signature types it chooses e-sign vs. an
  // already-signed manual upload.
  return DocumentsService.documentsConfirmCreate({
    id: uploadResponse.document_id,
    requestBody: { request_esignature: requestEsignature },
  });
}
